import { IRowWeightFinding, rowWeightsValidatorFn } from "../baysData/rowWeightsValidator";

import { IOpenVesselDefinitionV1 } from "open-vessel-definition";
import { cloneObject } from "@tedivo/tedivo-pure-helpers";
import { isPlainObject } from "../structure/patterns";

export interface IFixRowWeightsResult {
  /** The vessel without the implausible weights. The input object when nothing changed */
  json: IOpenVesselDefinitionV1;
  /** The weights dropped */
  dropped: IRowWeightFinding[];
  /** Entries that held nothing but `size` once their weight was gone, and were removed */
  entriesRemoved: number;
  changed: boolean;
}

/**
 * Drops the weights that `rowWeightsValidator` reports. An entry left with nothing but
 * its `size` is removed too, and an emptied `rowInfoByLength` with it: such an entry
 * carries no information, and for a 40'+ size it would still make the Bay look as if
 * it held 40s. The input is not modified.
 *
 * Runs before `fixBay40sLocation` in `repairOvd`, so placeholder weights never turn a
 * pair into a split one.
 */
export function fixRowWeights(json: IOpenVesselDefinitionV1): IFixRowWeightsResult {
  const { findings } = rowWeightsValidatorFn({
    data: { json, lcgVcgTcgAndPairings: undefined, lcgsBy20Bay: undefined },
  }).result;

  if (findings.length === 0) return { json, dropped: [], entriesRemoved: 0, changed: false };

  const newJson = cloneObject(json);
  let entriesRemoved = 0;

  const byBayLevel = new Map<string, IRowWeightFinding[]>();
  findings.forEach((f) => {
    const key = `${f.isoBay}-${f.level}`;
    byBayLevel.set(key, [...(byBayLevel.get(key) ?? []), f]);
  });

  (newJson.baysData as unknown[]).forEach((entry) => {
    if (!isPlainObject(entry)) return;
    const list = byBayLevel.get(`${entry["isoBay"]}-${entry["level"]}`);
    if (list === undefined) return;

    list.forEach((f) => {
      const dict =
        f.row === undefined
          ? entry["infoByContLength"]
          : isPlainObject(entry["perRowInfo"]) && isPlainObject(entry["perRowInfo"]["each"])
            ? (entry["perRowInfo"]["each"][f.row] as Record<string, unknown> | undefined)?.[
                "rowInfoByLength"
              ]
            : undefined;

      if (!isPlainObject(dict)) return;
      const e = dict[String(f.size)];
      if (!isPlainObject(e)) return;

      delete e[f.field];

      const remaining = Object.keys(e).filter((k) => k !== "size");
      if (remaining.length === 0) {
        delete dict[String(f.size)];
        entriesRemoved++;
      }
    });

    // An emptied rowInfoByLength is meaningless
    const each = isPlainObject(entry["perRowInfo"]) ? entry["perRowInfo"]["each"] : undefined;
    if (isPlainObject(each)) {
      Object.values(each).forEach((r) => {
        if (
          isPlainObject(r) &&
          isPlainObject(r["rowInfoByLength"]) &&
          Object.keys(r["rowInfoByLength"]).length === 0
        )
          delete r["rowInfoByLength"];
      });
    }
  });

  return { json: newJson, dropped: findings, entriesRemoved, changed: true };
}
